import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import parse from 'html-react-parser';

import useParams from './hooks/useParams';
import useFetch from './hooks/useFetch';

import SearchForm from './components/SearchForm';
import ExploreTab from './components/ExploreTab';
import ProductsTab from './components/ProductsTab';
import ContentTab from './components/ContentTab';

import { setTrackDataLayer } from '../../utils/index';

const SearchResults = ({
  mainTitle,
  contentTabEndpoint,
  exploreTabEndpoint,
  productsTabEndpoint,
  initialTabs,
  placeholderLabel,
  exploreSearchCopy
}) => {
  const tabsObject = JSON.parse(initialTabs);
  const [
    { query, tab, page, sort, filters },
    setQuery,
    setTab,
    setPage,
    setSort,
    setFilters
  ] = useParams();
  const [selectedTab, setSelectedTab] = useState({});

  useEffect(() => {
    if (!tab && tabsObject) {
      setSelectedTab(tabsObject[0]);
      setTab(tabsObject[0]);
    } else {
      setSelectedTab(tab);
      setTab(tab);
    }
  }, []);

  useEffect(() => {
    if (tab) {
      setSelectedTab(tab);
    }
  }, [tab]);

  const endpoints = {};

  endpoints[tabsObject[0].value] = exploreTabEndpoint;
  endpoints[tabsObject[1].value] = productsTabEndpoint;
  endpoints[tabsObject[2].value] = contentTabEndpoint;

  const { results, loading } = useFetch(endpoints, {
    query,
    tab,
    page,
    sort,
    filters
  });

  const [newResults, setNewResults] = useState({});

  useEffect(() => {
    if (results) {
      setNewResults(results);
    }
  }, [results]);

  const handleTabs = tab => {
    if (selectedTab !== tab) {
      setNewResults(results);
      setSelectedTab(tab);
      setTab(tab);
    }
  };

  const handleTextChange = e => {
    const value = e.target.value;
    setQuery(value);

    if (tabsObject[1].value === selectedTab.value) {
      const gaItems = results.resultsListing.map(item => item.gaModel);

      setTrackDataLayer({
        eventName: 'view_item_list',
        custom: {
          ecommerce: {
            items: gaItems
          }
        }
      });
    }
  };

  const clearFilters = () => {
    if (filters) {
      Object.keys(filters).map(filter => {
        filters[filter] = '';
      });
    }
  };

  const handleClear = () => {
    clearFilters();
    setQuery('');
  };

  const tabValidation =
    Object.keys(newResults).length && newResults.tabValue === selectedTab.value;

  return (
    <>
      <div className="search-results__title-container">
        <h1 className="search-results__title">{mainTitle}</h1>
      </div>
      <SearchForm
        placeholder={placeholderLabel}
        handleTextChange={handleTextChange}
        query={query}
        handleClear={handleClear}
      />
      <div
        className="search-results__tabs-container"
        role="tablist"
        aria-label={mainTitle}
      >
        {tabsObject.map((tab, i) => {
          return (
            <button
              key={i}
              role="tab"
              className={tab.value === selectedTab.value ? 'active' : ''}
              tabIndex={tab.value === selectedTab.value ? 0 : -1}
              aria-selected={tab.value === selectedTab.value}
              aria-controls={tab.value === selectedTab.value}
              onClick={() => handleTabs(tab, i)}
            >
              {tab.label}
            </button>
          );
        })}
      </div>
      {loading && <div className="results-listing__loading"></div>}
      <div className="search-results__tabs-content-container">
        {tabValidation ? (
          <ExploreTab
            tab={tabsObject[0]}
            endpoint={exploreTabEndpoint}
            selectedTab={selectedTab}
            refineSearchCopy={newResults.refineSearchCopy}
            exploreSearchCopy={parse(exploreSearchCopy)}
            resultsListing={newResults.resultsListing}
            loading={loading}
            resultsInfo={results.resultsInfo}
          />
        ) : null}
        {tabValidation ? (
          <ProductsTab
            tab={tabsObject[1]}
            selectedTab={selectedTab}
            resultsListing={newResults.resultsListing}
            loading={loading}
            page={page}
            sort={sort}
            filters={filters}
            resultsInfo={results.resultsInfo}
            sortOptions={newResults.sortOptions}
            currentPage={page}
            resultsPerPage={newResults.resultsPerPage}
            totalPages={newResults.totalPages}
            facets={newResults.facets}
            setPage={setPage}
            setSort={setSort}
            setFilters={setFilters}
          />
        ) : null}
        {tabValidation ? (
          <ContentTab
            tab={tabsObject[2]}
            selectedTab={selectedTab}
            resultsListing={newResults.resultsListing}
            loading={loading}
            page={page}
            sort={sort}
            filters={filters}
            resultsInfo={results.resultsInfo}
            sortOptions={newResults.sortOptions}
            currentPage={page}
            resultsPerPage={newResults.resultsPerPage}
            totalPages={newResults.totalPages}
            facets={newResults.facets}
            setPage={setPage}
            setSort={setSort}
            setFilters={setFilters}
          />
        ) : null}
      </div>
    </>
  );
};

SearchResults.propTypes = {
  facets: PropTypes.array,
  subTitle: PropTypes.string,
  resetBtnLabel: PropTypes.string
};

export default SearchResults;
